import { useMemo } from "react";
import type { SearchResult, SourceDiscoverItem, SourceDiscoverSection, SourceManifest } from "../api";

export function DiscoverSection({
  source,
  section,
  showTitle = true,
  onNavigate,
  onBookSelect,
}: {
  source: SourceManifest;
  section: SourceDiscoverSection;
  showTitle?: boolean;
  onNavigate: (targetUrl: string) => void;
  onBookSelect: (result: SearchResult) => void;
}) {
  if (section.layout === "nav") {
    return (
      <section className="space-y-2">
        {showTitle && <h2 className="text-sm font-semibold">{section.title}</h2>}
        <div className="flex flex-wrap gap-2">
          {section.items.map(item => (
            <DiscoverNavItem key={`${section.id}:${item.title}:${item.sourceUrl}`} item={item} onNavigate={onNavigate} />
          ))}
        </div>
      </section>
    );
  }

  if (section.layout === "featured") {
    return (
      <section className="space-y-2">
        {showTitle && <h2 className="text-sm font-semibold">{section.title}</h2>}
        <div className="grid gap-3 sm:grid-cols-2">
          {section.items.slice(0, 6).map(item => (
            <DiscoverFeaturedItem
              key={`${section.id}:${item.title}:${item.sourceBookId}`}
              source={source}
              item={item}
              onBookSelect={onBookSelect}
            />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-2">
      {showTitle && <h2 className="text-sm font-semibold">{section.title}</h2>}
      <div className="grid gap-1 sm:grid-cols-2">
        {section.items.slice(0, 12).map(item => (
          <DiscoverListItem
            key={`${section.id}:${item.title}:${item.sourceBookId}`}
            source={source}
            item={item}
            onBookSelect={onBookSelect}
          />
        ))}
      </div>
    </section>
  );
}

export function DiscoverNavItem({ item, active = false, onNavigate }: { item: SourceDiscoverItem; active?: boolean; onNavigate: (targetUrl: string) => void }) {
  const disabled = !item.sourceUrl;

  return (
    <button
      type="button"
      disabled={disabled}
      className={`rounded-full border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        active ? "bg-foreground text-background" : "bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
      }`}
      onClick={() => {
        if (item.sourceUrl) onNavigate(item.sourceUrl);
      }}
    >
      {item.title}
    </button>
  );
}

function DiscoverFeaturedItem({
  source,
  item,
  onBookSelect,
}: {
  source: SourceManifest;
  item: SourceDiscoverItem;
  onBookSelect: (result: SearchResult) => void;
}) {
  const result = useMemo(() => createDiscoverSearchResult(source, item), [source, item]);

  return (
    <button
      type="button"
      disabled={!result}
      className="grid grid-cols-[3.5rem_minmax(0,1fr)] gap-3 rounded-xl border bg-background p-2 text-left transition-colors hover:bg-accent/60 disabled:cursor-not-allowed disabled:opacity-50"
      onClick={() => {
        if (result) onBookSelect(result);
      }}
    >
      <div className="aspect-[3/4] overflow-hidden rounded-lg border bg-muted">
        {item.coverUrl && <img src={item.coverUrl} alt={item.title} className="h-full w-full object-cover" loading="lazy" />}
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold">{item.title}</p>
        <p className="mt-1 truncate text-xs text-muted-foreground">{item.author ?? item.category ?? "未知作者"}</p>
        <p className="mt-2 line-clamp-2 text-xs leading-5 text-muted-foreground">{item.description ?? "暂无简介"}</p>
      </div>
    </button>
  );
}

function DiscoverListItem({
  source,
  item,
  onBookSelect,
}: {
  source: SourceManifest;
  item: SourceDiscoverItem;
  onBookSelect: (result: SearchResult) => void;
}) {
  const result = useMemo(() => createDiscoverSearchResult(source, item), [source, item]);

  return (
    <button
      type="button"
      disabled={!result}
      className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent/70 disabled:cursor-not-allowed disabled:opacity-50"
      onClick={() => {
        if (result) onBookSelect(result);
      }}
    >
      <span className="min-w-0">
        <span className="block truncate">{item.title}</span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">{item.author ?? item.category ?? "未知作者"}</span>
      </span>
    </button>
  );
}

function createDiscoverSearchResult(source: SourceManifest, item: SourceDiscoverItem): SearchResult | null {
  if (!item.sourceBookId) return null;

  const bookId = `${source.id}:${item.sourceBookId}`;

  return {
    book: {
      id: bookId,
      title: item.title,
      author: item.author,
      kind: source.kinds[0] ?? "novel",
      coverUrl: item.coverUrl,
      description: item.description,
      tags: item.category ? [item.category] : undefined,
    },
    sources: [
      {
        id: bookId,
        bookId,
        sourceId: source.id,
        sourceBookId: item.sourceBookId,
      },
    ],
  };
}
